import { Col, Divider, Row, notification } from 'antd';
import React, { Fragment, useEffect, useState } from 'react';
import Stepper from '../common/Stepper';
import { AbhaDashBoardState, FetchState } from '../abha/types';
import YoutubeVideoPlayerV2 from '../YoutubeVideoPlayerV2';
import AbhaIncentiveCalculator from './AbhaIncentiveCalculator';
import {
    AbhaIconNotLinked,
    RupeeIcon,
    SuccessManagerIcon,
    downloadIcon,
    loaderIcon,
    qrCodeIcon,
} from '../common/Icons';
import { captureError } from '../../pages/captureError';
import { getAllPatientsAfterAMonth, getDoc } from '../../store/idbHandler';
import { toShortDateString } from '../../store/utils';
import { abhaYoutubeVidoes, csColors } from '../../constants';
import GoToBookingBtn from '../common/GoToBookingBtn';
import { getPtsWithAbha } from '../../pages/allPatientsIndex';
import { AnimatePresence } from 'framer-motion';
import SeeCalculation from './SeeCalculation';
import moment from 'moment';
import { all_states } from '../abha/util';
import { IncentiveIssues, dhis_states } from '../../api/getAbhaMetricsDashboard';
import DHISCard from './DHISCard';
import IncentiveCard from './IncentiveCard';
import { updateIncentiveStatus } from '../../api/abha/incentive/update_status';
import ClinicDetailsContainer from '../abha/ClinicDetailsContainer';
import ClinicSelector from '../common/ClinicSelector';
import fetchAbhaQr from '../../api/fetchAbhaQr';
import { track } from '../../tracking';
import { fetchAndDownloadAbhaQrController } from '../abha/fetchAbhaQr';

const ABHADashboardV2: React.FC<{
    abhaMetricData: AbhaDashBoardState;
    defaultTab?: string;
    currMonthLoss: number | undefined;
    currClinic: string | undefined;
    dhisStatus: dhis_states | undefined;
    setDhisStatus: (val?: dhis_states) => void;
    fetchState: FetchState | undefined;
    dhisIssues: IncentiveIssues | undefined;
}> = ({
    abhaMetricData,
    defaultTab,
    currMonthLoss,
    currClinic,
    dhisStatus,
    setDhisStatus,
    fetchState,
    dhisIssues,
}) => {
    const [selectedMonth, setSelectedMonth] = useState<string | undefined>(defaultTab);
    const [currVideoId, setCurrVideoId] = useState<string>();
    const [ptCount, setPtCount] = useState({
        allPt: 0,
        ptWithAbha: 0,
        recordsLinked: 0,
        isPtDataLoading: false,
    });
    const [showCalc, setShowCalc] = useState(false);
    const [incentiveStatus, setIncentiveStatus] = useState<
        {
            status?: all_states;
            month?: string;
            amount?: number;
            issue?: IncentiveIssues;
            dhisstatus?: dhis_states;
            dhisissues?: IncentiveIssues;
        }[]
    >([]);

    const [isIncentiveStatusUpdating, setIncentiveStatusUpdating] = useState(false);
    const [abhaQrStatus, setIsAbhaQrLoading] = useState<{
        status: boolean;
        cid: string | undefined;
    }>({
        status: false,
        cid: currClinic,
    });

    useEffect(() => {
        setSelectedMonth(defaultTab);
    }, [defaultTab]);

    useEffect(() => {
        setPtCount((prev) => ({ ...prev, isPtDataLoading: true }));

        if (selectedMonth) {
            const selectedMonthMoment = moment(selectedMonth, 'MMMM, YYYY');

            getAllPatientsAfterAMonth(selectedMonthMoment).then((pts) => {
                const currMonthPts = pts?.filter((pt) => !pt.archived) || [];

                setPtCount((prev) => ({
                    ...prev,
                    allPt: currMonthPts.length,
                    ptWithAbha: currMonthPts.filter((c) => c.abha?.id).length,
                    recordsLinked: currMonthPts
                        .filter((c) => c.abha?.id)
                        .reduce((prev, curr) => {
                            prev = prev + (curr.abha?.records ?? 0);
                            return prev;
                        }, 0),
                    isPtDataLoading: false,
                }));
            });
        }
    }, [selectedMonth]);

    useEffect(() => {
        if (fetchState === 'success') {
            for (let i = 0; i < abhaMetricData.monthlyAchieved.length; i++) {
                const currMonthAcheived = abhaMetricData.monthlyAchieved[i];

                if (currMonthAcheived && currMonthAcheived?.incentiveStatus) {
                    setIncentiveStatus((prev) => [
                        ...prev,
                        {
                            status: currMonthAcheived.incentiveStatus,
                            month: currMonthAcheived.month,
                            amount: currMonthAcheived.incentive,
                            issue: currMonthAcheived.issue,
                            dhisissues: dhisIssues,
                            dhisstatus: dhisStatus,
                        },
                    ]);
                } else {
                    setIncentiveStatus((prev) => [
                        ...prev,
                        {
                            dhisissues: dhisIssues,
                            dhisstatus: dhisStatus,
                            amount: currMonthAcheived?.incentive,
                        },
                    ]);
                }
            }
        }
    }, [fetchState]);

    const currVal = abhaMetricData.monthlyAchieved?.find((val) => val.month === selectedMonth);

    const dashBoardData = {
        ptAdded: [
            {
                title: <p className="font-400 text-14">Patients Added</p>,
                value: <p className="font-700 text-20">{ptCount.allPt.toLocaleString('en-IN')}</p>,
            },
            {
                title: <p className="font-400 text-14">Added with ABHA</p>,
                value: (
                    <p className="font-700 text-20">{ptCount.ptWithAbha.toLocaleString('en-IN')}</p>
                ),
            },
        ],
        ptAbha: [
            {
                title: <p className="font-400 text-14">Patients without ABHA</p>,
                value: (
                    <p className="font-700 text-20 text-red-primary">
                        {(ptCount.allPt - ptCount.ptWithAbha).toLocaleString('en-IN')}
                    </p>
                ),
            },
            {
                title: <p className="font-400 text-14">Potential Loss</p>,
                value: (
                    <p className="font-700 text-20 text-red-primary">
                        ₹ {((ptCount.allPt - ptCount.ptWithAbha) * 80).toLocaleString('en-IN')}
                    </p>
                ),
            },
        ],
        incentive: [
            {
                title: <p className="font-400 text-14">Incentive Eligible</p>,
                value: (
                    <p className="font-700 text-20">
                        ₹{' '}
                        {currVal?.incentive
                            ? abhaMetricData!
                                  .monthlyAchieved!.find((val) => val.month === selectedMonth)!
                                  .incentive!.toLocaleString('en-IN')
                            : 0}
                    </p>
                ),
            },
            {
                title: <p className="font-400 text-14">Records eligible for incentive</p>,
                value: (
                    <p className="flex gap-8 items-end">
                        <span className="font-700 text-20">
                            {currVal?.records_eligible
                                ? currVal?.records_eligible.toLocaleString('en-IN')
                                : 0}
                        </span>
                        <span
                            onClick={() => setShowCalc(true)}
                            className="relative bottom-2 cursor-pointer text-darwin-accent-bright-blue-800 font-700 text-12"
                        >
                            See calculation
                        </span>
                    </p>
                ),
            },
        ],
    };

    const ptsWithAbha = getPtsWithAbha();

    const closeModal = () => setCurrVideoId(undefined);

    const doc = getDoc();

    const finalDashboardData =
        abhaMetricData.monthlyAchieved && abhaMetricData.monthlyAchieved?.length > 0
            ? dashBoardData
            : { ptAdded: dashBoardData.ptAdded };

    const changeIncentiveStatus = (
        result: {
            status?: all_states;
            month?: string;
            amount?: number;
            issue?: IncentiveIssues;
            dhisstatus?: dhis_states;
            dhisissues?: IncentiveIssues;
        },
        skipMonth?: boolean,
        onSuccess?: () => void,
        onFailure?: () => void,
    ): void => {
        let dateString, date;

        if (!skipMonth && selectedMonth) {
            date = new Date(moment(selectedMonth, 'MMMM, YYYY').toISOString());
        } else {
            if (result.month) {
                date = new Date(moment(result.month, 'MMMM, YYYY').toISOString());
            }
        }

        if (date) {
            const year = date.toLocaleString('default', {
                year: 'numeric',
            });

            const month = date.toLocaleString('default', {
                month: '2-digit',
            });

            dateString = `${year}${month}`;
        }

        setIncentiveStatusUpdating(true);

        const amount = abhaMetricData.monthlyAchieved?.find((val) => val.month === selectedMonth)
            ?.incentive;

        updateIncentiveStatus({
            cid: currClinic ?? '',
            month: dateString,
            state_id: result.status,
            issue: result.issue,
            dhisstatus: result.dhisstatus,
            dhisissues: result.dhisissues,
        })
            .then((res) => {
                if (res.success) {
                    onSuccess
                        ? onSuccess()
                        : notification.success({
                              message: `Status updated for ${result.month ?? selectedMonth}!`,
                              duration: 2,
                          });

                    if (result.status === 'incentive-not-recieved') {
                        try {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            $crisp.push([
                                'do',
                                'message:send',
                                [
                                    'text',
                                    `ABHA Issue for ${
                                        result.month ?? selectedMonth
                                    } => Incentive not recieved`,
                                ],
                            ]);
                        } catch (error) {
                            console.error(error);
                        }
                    }

                    setIncentiveStatus((prev) => [
                        ...prev.filter((p) => p.month !== result.month),
                        {
                            status: result.status,
                            amount: amount,
                            month: result.month ?? selectedMonth,
                            issue: result.issue,
                            dhisstatus: result.dhisstatus,
                            dhisissues: result.dhisissues,
                        },
                    ]);
                } else {
                    throw new Error(res.error);
                }
            })
            .catch((e) => {
                captureError(e);

                onFailure
                    ? onFailure()
                    : notification.error({
                          message: `Error occured updating status for ${
                              result.month ?? selectedMonth
                          }`,
                          duration: 2,
                      });
            })
            .finally(() => setIncentiveStatusUpdating(false));
    };

    const currDateMonth = incentiveStatus.find((curr) => curr.month === selectedMonth);
    const currDhisStatus = incentiveStatus
        .filter(
            (curr) =>
                curr?.dhisstatus === 'dhis-approval' || curr?.dhisstatus === 'dhis-registration',
        )
        .slice(-1)?.[0];

    const isStaff = doc?.loginmeta?.role && doc?.loginmeta?.role === 'STAFF';

    const incentiveLastMonth = currVal?.incentive
        ? abhaMetricData?.monthlyAchieved?.find((val) => {
              const currMonthDate = new Date(moment(val.month, 'MMMM, YYYY').toISOString());
              const selectedMonthDate = new Date(moment(selectedMonth, 'MMMM, YYYY').toISOString());

              return (
                  moment(currMonthDate).month() ===
                  moment(selectedMonthDate).clone().subtract(1, 'month').month()
              );
          })?.incentive
        : 0;

    const showCards = currMonthLoss && currMonthLoss <= 0 && !!incentiveLastMonth;

    const onAbhaQrDownload = () => {
        const clinicId = abhaQrStatus.cid;

        if (abhaQrStatus.status === true) {
            return;
        }

        setIsAbhaQrLoading((prev) => ({ ...prev, status: true }));

        if (clinicId) {
            fetchAndDownloadAbhaQrController(clinicId)
                .then(() => {
                    setIsAbhaQrLoading({ status: false, cid: clinicId });
                })
                .catch(() => {
                    notification.error({
                        message: 'Failed to download',
                        duration: 5,
                        placement: 'topRight',
                    });
                    setIsAbhaQrLoading({ status: false, cid: clinicId });
                });
        }
    };

    return (
        <div className="w-full h-full flex flex-col items-center">
            <div className="w-full gap-20 flex flex-col items-center">
                {currMonthLoss && currMonthLoss <= 0 ? null : (
                    <Stepper
                        className="w-full h-48 max-h-48 min-h-48 overflow-x-auto hide-scrollbar"
                        items={[
                            { title: 'Enrol into ABDM', checked: true },
                            {
                                title: 'Add patients with ABHA & link records',
                                checked: ptsWithAbha ? ptsWithAbha > 10 : false,
                            },
                            {
                                title: 'Link more than 100 records with ABHA to get incentives!',
                                hideTag: true,
                            },
                        ]}
                        showTag={true}
                        innerClassName="w-400 max-w-400 max-h-52"
                        // separatorClassName="border-darwin-accent-bright-blue-800"
                        separatorClassName="border-darwin-neutral_new-200"
                    />
                )}
            </div>
            <div
                className={`flex flex-col gap-12 w-full bg-darwin-accent-bright-blue-100 rounded-8 p-16 ${
                    currMonthLoss && currMonthLoss <= 0 ? '' : 'mt-24'
                }`}
            >
                <div className="flex overflow-hidden hover:overflow-x-auto px-8 w-full items-center">
                    {abhaMetricData.monthlyAchieved?.map((data, idx) => (
                        <Fragment>
                            <p
                                className={`px-8 whitespace-nowrap cursor-pointer ${
                                    selectedMonth === data.month
                                        ? 'font-700 text-16 text-darwin-accent-bright-blue-800'
                                        : 'font-400 text-14'
                                }`}
                                onClick={() => setSelectedMonth(data.month)}
                                key={data.month}
                            >
                                {toShortDateString(
                                    data.month
                                        ? new Date(moment(data.month, 'MMMM, YYYY').toISOString())
                                        : new Date(),
                                )}
                            </p>
                            {idx !== abhaMetricData.monthlyAchieved?.length - 1 && (
                                <Divider type="vertical" className="h-full border-l-2" />
                            )}
                        </Fragment>
                    ))}
                </div>

                {isStaff || fetchState === 'loading' || fetchState === 'error'
                    ? null
                    : dhisStatus === 'dhis-end'
                    ? currDateMonth &&
                      currDateMonth.status &&
                      showCards && (
                          <IncentiveCard
                              onStatusChange={changeIncentiveStatus}
                              incentive={incentiveLastMonth}
                              selectedMonth={selectedMonth}
                              incentiveDateMonth={currDateMonth}
                              dhisStatus={dhisStatus}
                          />
                      )
                    : null}

                {isStaff || fetchState === 'loading' || fetchState === 'error'
                    ? null
                    : showCards &&
                      dhisStatus !== 'dhis-end' && (
                          <DHISCard
                              onStatusChange={changeIncentiveStatus}
                              currMonthStatus={currDhisStatus}
                              setDhisStatus={setDhisStatus}
                              dhisStatus={dhisStatus}
                          />
                      )}
                <div className="flex items-center gap-16 self-start w-full overflow-auto whitespace-nowrap hide-scrollbar">
                    {ptCount.isPtDataLoading ? (
                        <div className="px-16 w-full">
                            <p className="flex items-center gap-8">Loading data...</p>
                        </div>
                    ) : (
                        Object.keys(finalDashboardData).map((key: string) => (
                            <div
                                className={`border gap-12 bg-white flex justify-between items-center border-darwin-neutral_new-200 rounded-8 py-12 px-16 ${
                                    key === 'incentive' ? 'abha-linked-inner-more' : 'bg-white'
                                } ${
                                    abhaMetricData.monthlyAchieved &&
                                    abhaMetricData.monthlyAchieved?.length > 0
                                        ? 'flex-grow'
                                        : 'min-w-328'
                                }`}
                            >
                                {finalDashboardData[key as keyof typeof finalDashboardData].map(
                                    (d, idx) => (
                                        <div
                                            className={`flex flex-col flex-grow items-start gap-4 ${
                                                idx !==
                                                finalDashboardData[
                                                    key as keyof typeof finalDashboardData
                                                ].length -
                                                    1
                                                    ? 'border-r border-darwin-neutral_new-200'
                                                    : ''
                                            }`}
                                        >
                                            <div
                                                className={`flex flex-col flex-grow ${
                                                    idx !==
                                                    finalDashboardData[
                                                        key as keyof typeof finalDashboardData
                                                    ].length -
                                                        1
                                                        ? 'mr-8'
                                                        : ''
                                                }`}
                                            >
                                                <p
                                                    className={`${
                                                        key === 'incentive'
                                                            ? 'font-700'
                                                            : 'font-400'
                                                    } text-13`}
                                                >
                                                    {d.title}
                                                </p>
                                                {d.value}
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
            <div className="flex w-full mt-24 gap-12 flex-col">
                <Row gutter={[12, 12]}>
                    <Col span={8} className="flex-shrink-0 max-h-304">
                        <AbhaIncentiveCalculator />
                    </Col>
                    {abhaYoutubeVidoes.map((v) => (
                        <Col span={8} className="hover:opacity-80 max-h-304 flex-shrink-0">
                            <div
                                onClick={() => setCurrVideoId(v.id)}
                                className="flex cursor-pointer bg-white items-center gap-8 p-12 rounded-t-8 border border-darwin-neutral_new-200"
                            >
                                {AbhaIconNotLinked}
                                <p className="font-400 text-18">{v.title}</p>
                            </div>
                            <div
                                className="rounded-b-8 h-253 max-h-253 cursor-pointer"
                                onClick={() => setCurrVideoId(v.id)}
                            >
                                <img
                                    className="w-full h-full rounded-b-8"
                                    src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                                />
                                <span
                                    className="absolute p-16 rounded-full hover:bg-darwin-neutral-1000 hover:opacity-20"
                                    style={{ left: '42%', bottom: '32%' }}
                                >
                                    <svg
                                        width={40}
                                        height={40}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 384 512"
                                        fill="white"
                                    >
                                        <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z" />
                                    </svg>
                                </span>
                            </div>
                        </Col>
                    ))}
                </Row>
                <Row gutter={[12, 12]}>
                    <Col span={8}>
                        <ClinicDetailsContainer hideAbhaQRDownload />
                    </Col>
                    <Col span={8}>
                        <div className="rounded-8 bg-white flex flex-col h-240 max-h-240 border border-darwin-neutral_new-200">
                            <div className="flex rounded-t-8 px-16 py-12 gap-16 bg-darwin-neutral_new-50 flex-col items-start">
                                <div className="flex items-center gap-8">
                                    <span>
                                        {qrCodeIcon('w-22 h-22 fill-darwin-accent-bright-blue-800')}
                                    </span>
                                    <p className="text-14 font-700">
                                        Instant appointments for patients
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-20 h-full px-16 pb-16 pt-12">
                                <img
                                    src="/images/qr-code-img.png"
                                    width={120}
                                    className="rounded-4 h-full"
                                />
                                <div className="flex flex-col self-start gap-16">
                                    <p className="text-14 font-400">
                                        Patients can generate ABHA and book appointments using this
                                        QR Code.
                                    </p>
                                    <ClinicSelector
                                        currClinicId={abhaQrStatus.cid}
                                        onClinicSelect={(clinicId) =>
                                            setIsAbhaQrLoading({ cid: clinicId, status: false })
                                        }
                                        className="flex rounded-8 items-center h-36 justify-between px-8 bg-darwin-neutral_new-50 border-neutral-5 rounded-4 text-14 w-full"
                                        showOnlyAbhaClinics
                                    />
                                    <div
                                        className={`flex text-13 gap-8 px-8 py-4 rounded-4 font-700 justify-between items-center border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 ${
                                            abhaQrStatus.status === true ? '' : 'cursor-pointer'
                                        }`}
                                        onClick={onAbhaQrDownload}
                                        style={{ width: 'fit-content' }}
                                    >
                                        Download{' '}
                                        <span>
                                            {abhaQrStatus.status === true
                                                ? loaderIcon(
                                                      'w-14 h-14 fill-darwin-accent-bright-blue-800 font-700 animate-spin',
                                                  )
                                                : downloadIcon(
                                                      'w-14 h-14 fill-darwin-accent-bright-blue-800',
                                                  )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div
                            className={`flex max-h-240 h-240 justify-center rounded-8 gap-12 px-16 py-24 flex-col items-start border border-darwin-neutral_new-200 ${csColors.PLUS.innerBg}`}
                        >
                            <span>{SuccessManagerIcon('w-20 h-20')}</span>
                            <div className="flex flex-col items-start gap-12">
                                <div className="flex flex-col items-start gap-4">
                                    <p className="font-700 text-16">Need help with ABHA? </p>
                                    <p className="font-700 text-14 text-darwin-neutral_new-800">
                                        Connect with your success manager now.
                                    </p>
                                </div>
                                <GoToBookingBtn />
                            </div>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div
                            className={`flex h-200 border border-darwin-neutral_new-200 max-h-200 justify-center rounded-8 gap-8 px-12 py-24 flex-col items-start bg-darwin-neutral_new-50`}
                        >
                            <div className="flex w-full justify-between items-center">
                                <div className="flex justify-between items-center gap-8">
                                    <span className="p-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 640 512"
                                            className="w-18 h-18"
                                        >
                                            <path d="M352 128C352 198.7 294.7 256 224 256C153.3 256 96 198.7 96 128C96 57.31 153.3 0 224 0C294.7 0 352 57.31 352 128zM144 128C144 172.2 179.8 208 224 208C268.2 208 304 172.2 304 128C304 83.82 268.2 48 224 48C179.8 48 144 83.82 144 128zM176 352C170.6 352 165.2 352.3 160 352.1V402.3C183.1 409.2 200 430.6 200 456C200 486.9 174.9 512 144 512C113.1 512 88 486.9 88 456C88 430.6 104.9 409.2 128 402.3V361.3C81.09 380.3 48 426.3 48 480V488C48 501.3 37.25 512 24 512C10.75 512 0 501.3 0 488V480C0 382.8 78.8 304 176 304H272C369.2 304 448 382.8 448 480V488C448 501.3 437.3 512 424 512C410.7 512 400 501.3 400 488V480C400 426.3 366.9 380.3 320 361.3V402.3C343.1 409.2 360 430.6 360 456V488C360 496.8 352.8 504 344 504C335.2 504 328 496.8 328 488V456C328 442.7 317.3 432 304 432C290.7 432 280 442.7 280 456V488C280 496.8 272.8 504 264 504C255.2 504 248 496.8 248 488V456C248 430.6 264.9 409.2 288 402.3V352.1C282.8 352.3 277.4 352 272 352H176zM144 432C130.7 432 120 442.7 120 456C120 469.3 130.7 480 144 480C157.3 480 168 469.3 168 456C168 442.7 157.3 432 144 432zM592 0C618.5 0 640 21.49 640 48V176C640 202.5 618.5 224 592 224H544L460.8 286.4C458.4 288.2 455.1 288.5 452.4 287.2C449.7 285.8 448 283 448 280V224H432C405.5 224 384 202.5 384 176V48C384 21.49 405.5 0 432 0L592 0z"></path>
                                        </svg>
                                    </span>
                                    <p className="text-18 font-700">Doctors earning on EKA</p>
                                </div>
                                <p className="text-33 font-700">264+</p>
                            </div>
                            <Divider />
                            <div className="flex w-full justify-between items-center">
                                <div className="flex justify-between items-center gap-8">
                                    <p className="flex gap-4">
                                        <span className="p-4 self-center">
                                            {RupeeIcon('w-18 h-18')}
                                        </span>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center">
                                                <p className="text-18 font-700">Total earnings</p>
                                            </div>
                                            <p className="text-12 font-700">(till 31st Jan 2024)</p>
                                        </div>
                                    </p>
                                </div>
                                <p className="text-33 text-darwin-green-primary font-700">
                                    ₹22,48,700
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row
                    gutter={[12, 12]}
                    className="flex flex-col flex-shrink-0 w-full"
                    style={{ height: 3000 }}
                >
                    <Col span={24} className="p-0">
                        <iframe
                            className="relative w-full h-full flex-shrink-0"
                            src="https://product.eka.care/iframes/dhis-learning"
                        />
                    </Col>
                </Row>
            </div>

            <YoutubeVideoPlayerV2
                closeModal={closeModal}
                currVideoId={currVideoId}
                visible={!!currVideoId}
            />

            <AnimatePresence>
                {showCalc && (
                    <SeeCalculation
                        close={() => setShowCalc(false)}
                        recordCreated={currVal?.records_linked ?? 0}
                        uniqueRecords={currVal?.records_eligible ?? 0}
                        linkingStatus={{
                            linked: currVal?.records_linked ?? 0,
                            errored: currVal?.records_errored ?? 0,
                            inprogress: currVal?.records_in_progress ?? 0,
                        }}
                        eligible={currVal?.records_eligible ?? 0}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default ABHADashboardV2;
